import { ExternalLink } from "lucide-react";
import { staffUi } from "@/pages/applications/staff-ui-classes";

/** Mirrors applications/sos_lookup.py API payload */
export interface SosRegistryApiPayload {
  eligible?: boolean;
  status?: string;
  status_display?: string;
  message?: string | null;
  requested_legal_name?: string;
  requested_state?: string;
  updated_at?: string | null;
  registry_scraped_at?: string | null;
  vendor_cost?: number | null;
  state_scraper_version?: string | null;
  scraper_software_updated_at?: string | null;
  skip_reason?: string | null;
  fields?: { key: string; label: string; value: string | null }[];
  officers?: { name?: string; title?: string }[];
}

type Props = {
  data: SosRegistryApiPayload;
};

/** Application detail only — Secretary of State verification block */
export function SosRegistryPanel({ data }: Props) {
  if (!data?.eligible) return null;

  const fields = data.fields ?? [];
  const officers = data.officers ?? [];

  return (
    <div className={staffUi.sosPanel}>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-600">
          State business registry
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Pulled from the Secretary of State for the legal name and state on file (non‑sole‑proprietor businesses).
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-slate-800">{data.status_display ?? data.status}</span>
        {data.requested_legal_name && (
          <span className="text-xs text-muted-foreground">
            Lookup: <span className="font-medium text-slate-700">{data.requested_legal_name}</span>
            {data.requested_state ? ` (${data.requested_state})` : ""}
          </span>
        )}
      </div>

      {data.message && (
        <p className="text-sm text-slate-700">{data.message}</p>
      )}

      {(data.registry_scraped_at || data.updated_at || data.state_scraper_version || data.vendor_cost != null) && (
        <div className="text-[11px] text-muted-foreground space-y-0.5 border-t border-slate-200 pt-2">
          {data.registry_scraped_at && (
            <p>
              <span className="font-semibold text-slate-600">Registry data scraped at:</span>{" "}
              {data.registry_scraped_at}
            </p>
          )}
          {data.updated_at && (
            <p>
              <span className="font-semibold text-slate-600">Last checked:</span>{" "}
              {new Date(data.updated_at).toLocaleString()}
            </p>
          )}
          {data.state_scraper_version && (
            <p>
              <span className="font-semibold text-slate-600">Scraper version:</span>{" "}
              {data.state_scraper_version}
            </p>
          )}
          {data.scraper_software_updated_at && (
            <p>
              <span className="font-semibold text-slate-600">Scraper software updated:</span>{" "}
              {new Date(data.scraper_software_updated_at).toLocaleString()}
            </p>
          )}
          {data.vendor_cost != null && (
            <p>
              <span className="font-semibold text-slate-600">Lookup cost:</span> $
              {Number(data.vendor_cost).toFixed(4)}
            </p>
          )}
          {data.skip_reason && (
            <p>
              <span className="font-semibold text-slate-600">Skip reason:</span>{" "}
              {data.skip_reason.replace(/_/g, " ")}
            </p>
          )}
        </div>
      )}

      <div className="border-t border-slate-200 pt-2 space-y-2">
        <p className="text-xs font-semibold text-slate-700">Registry fields</p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          {fields.map((row) => (
            <div key={row.key} className="min-w-0">
              <dt className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{row.label}</dt>
              <dd className="text-slate-900 break-words">
                {row.key === "sos_url" && row.value && row.value !== "—" ? (
                  <a
                    href={row.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    Open state record <ExternalLink className="w-3 h-3 shrink-0" aria-hidden />
                  </a>
                ) : (
                  row.value ?? "—"
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="border-t border-slate-200 pt-2">
        <p className="text-xs font-semibold text-slate-700 mb-1">Officers / managers (from registry)</p>
        {officers.length === 0 ? (
          <p className="text-sm text-muted-foreground">—</p>
        ) : (
          <ul className="text-sm space-y-1">
            {officers.map((o, i) => (
              <li key={i}>
                <span className="font-medium">{o.name || "—"}</span>
                {o.title ? <span className="text-muted-foreground"> — {o.title}</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
